/**
 * Local IndexedDB cache of story posts visible to this device.
 *
 * Threat model: the cache holds (a) the encrypted blob (server-supplied,
 * opaque), (b) the StoryKey received via pairwise E2EE, and (c) per-story
 * metadata. Stories are local-only — cross-device replication of stories must
 * re-distribute the StoryKey through that device's pairwise E2EE channel.
 *
 * Hardening properties — mirroring iOS StoryStore.swift:
 *
 * 1. **TTL enforced on every read.** `story(id, at)` checks `createdAt + 24h`
 *    and *auto-purges the IndexedDB record* when the story has expired. A
 *    forensic image of the browser storage after expiry does not recover the
 *    StoryKey or the encrypted blob.
 *
 * 2. **Per-story hashed key.** The IndexedDB key is `story:<sha256hex(storyId)>`
 *    so the store does not enumerate storyIds in plaintext.
 *
 * 3. **Reactions are LWW by `ts`.** `recordReaction` discards stale arrivals.
 *    Empty `emoji` revokes (removes the entry).
 *
 * 4. **Per-story isolation.** Each story is stored under its own key; a purge
 *    of one story does not affect others.
 *
 * Mirrors iOS `kaether_for_ios/Stoat/E2EE/StoryStore.swift`.
 */

import { get, set, del, keys, createStore } from "idb-keyval";
import type { StoryMetadata } from "./Story";
import { STORY_TTL_SECONDS } from "./Story";
import type { StoryKeyDistributionPayload } from "./StoryKeyDistribution";
import type { StoryReactionPayload } from "./StoryReaction";

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export interface LocalStory {
  readonly metadata: StoryMetadata;
  /** AES-GCM combined (nonce ‖ ciphertext ‖ tag), as base64. */
  readonly encryptedBlobB64: string;
  /** Raw 32-byte StoryKey, as base64. */
  readonly storyKeyB64: string;
  /** ISO 8601 string when the user first viewed this story, or null. */
  readonly viewedAt: string | null;
}

/** On-disk envelope: the story + its reactions map. */
interface StoryEnvelope {
  readonly story: LocalStory;
  /** reactingUserId → latest StoryReactionPayload (LWW by ts). */
  reactions: Record<string, StoryReactionPayload>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether-story-store";
const DEFAULT_STORE_NAME = "stories";

/**
 * IndexedDB-backed store for locally-cached story posts.
 *
 * Constructing with a unique `dbName` gives an isolated store for testing.
 * Mirrors iOS `StoryStore`.
 */
export class StoryStore {
  private readonly idb: ReturnType<typeof createStore>;

  constructor(dbName = DEFAULT_DB_NAME) {
    this.idb = createStore(dbName, DEFAULT_STORE_NAME);
  }

  // -------------------------------------------------------------------------
  // Acceptance
  // -------------------------------------------------------------------------

  /**
   * Records an incoming story (a StoryKey distribution + the encrypted blob
   * the server delivered). Returns `true` if this is a new observation and
   * `false` if we already had it (idempotent — re-receiving the distribution
   * is a no-op).
   *
   * Silently drops distributions that have already expired by `at`.
   *
   * Mirrors iOS `StoryStore.acceptDistribution(_:encryptedBlob:at:)`.
   */
  async acceptDistribution(
    payload: StoryKeyDistributionPayload,
    encryptedBlobB64: string,
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<boolean> {
    const key = await idbKey(payload.storyId);
    const existing = await get<StoryEnvelope>(key, this.idb);
    if (existing !== undefined) {
      return false;
    }
    // Do not record distributions that are already expired.
    const expiresAt = payload.metadata.createdAt + STORY_TTL_SECONDS;
    if (at > expiresAt) {
      return false;
    }
    const story: LocalStory = {
      metadata: payload.metadata,
      encryptedBlobB64,
      storyKeyB64: payload.storyKey,
      viewedAt: null,
    };
    const envelope: StoryEnvelope = { story, reactions: {} };
    await set(key, envelope, this.idb);
    return true;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /**
   * Returns the `LocalStory` IFF it is still within its 24h TTL.
   *
   * If the story has expired, the IndexedDB record is **purged in the same
   * logical operation** before returning null — so a forensic image of the
   * browser storage after expiry does not recover the StoryKey or the
   * encrypted blob.
   *
   * `at` defaults to the real wall clock; pass an explicit Unix-second value
   * in tests for deterministic runs.
   *
   * Mirrors iOS `StoryStore.story(id:at:)`.
   */
  async story(
    id: string,
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<LocalStory | null> {
    const key = await idbKey(id);
    const envelope = await get<StoryEnvelope>(key, this.idb);
    if (envelope === undefined) return null;

    const expiresAt = envelope.story.metadata.createdAt + STORY_TTL_SECONDS;
    if (at > expiresAt) {
      // Auto-purge: same-operation delete before returning null.
      await del(key, this.idb);
      return null;
    }
    return envelope.story;
  }

  /**
   * Marks a story as viewed at `at` (ISO 8601). No-op if the story is not in
   * the store. The viewedAt is purely UX state (greying out the ring); it is
   * not signed or replicated.
   *
   * Mirrors iOS `StoryStore.markViewed(storyId:at:)`.
   */
  async markViewed(
    storyId: string,
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<void> {
    const key = await idbKey(storyId);
    const envelope = await get<StoryEnvelope>(key, this.idb);
    if (envelope === undefined) return;
    const updated: StoryEnvelope = {
      story: { ...envelope.story, viewedAt: new Date(at * 1000).toISOString() },
      reactions: envelope.reactions,
    };
    await set(key, updated, this.idb);
  }

  // -------------------------------------------------------------------------
  // Reactions
  // -------------------------------------------------------------------------

  /**
   * Records a reaction. LWW by `ts` — a stale-ts reaction from the same user
   * is discarded. Empty emoji revokes (drops the entry).
   *
   * Returns `true` if the store changed, `false` if it was a no-op (stale ts,
   * story not found, or story expired).
   *
   * Mirrors iOS `StoryStore.recordReaction(_:at:)`.
   */
  async recordReaction(
    p: StoryReactionPayload,
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<boolean> {
    const key = await idbKey(p.storyId);
    const envelope = await get<StoryEnvelope>(key, this.idb);
    if (envelope === undefined) return false;

    // Don't record reactions on stories that have already expired.
    const expiresAt = envelope.story.metadata.createdAt + STORY_TTL_SECONDS;
    if (at > expiresAt) {
      await del(key, this.idb);
      return false;
    }

    const existing = envelope.reactions[p.reactingUserId];
    if (existing !== undefined && existing.ts >= p.ts) {
      // Stale or equal-ts — keep the prior one (equal-ts → first writer wins).
      return false;
    }

    const reactions = { ...envelope.reactions };
    if (p.emoji.length === 0) {
      // Revoke: remove the entry only if there was one to remove.
      if (existing === undefined) return false;
      delete reactions[p.reactingUserId];
    } else {
      reactions[p.reactingUserId] = p;
    }
    await set(key, { ...envelope, reactions }, this.idb);
    return true;
  }

  /**
   * Returns reactions sorted by `reactingUserId` — a stable ordering that
   * does not depend on object property order.
   *
   * Mirrors iOS `StoryStore.reactions(storyId:)`.
   */
  async reactions(storyId: string): Promise<StoryReactionPayload[]> {
    const key = await idbKey(storyId);
    const envelope = await get<StoryEnvelope>(key, this.idb);
    if (envelope === undefined) return [];
    return Object.values(envelope.reactions).sort((a, b) =>
      a.reactingUserId.localeCompare(b.reactingUserId),
    );
  }

  // -------------------------------------------------------------------------
  // Maintenance
  // -------------------------------------------------------------------------

  /**
   * Removes any story whose `createdAt + 24h` is past `at`. Designed to be
   * called on app launch and periodically (e.g. once an hour) so the cache
   * does not grow unbounded.
   *
   * Mirrors iOS `StoryStore.purgeExpired(at:)`.
   */
  async purgeExpired(
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<void> {
    const allKeys = await keys<string>(this.idb);
    for (const k of allKeys) {
      const envelope = await get<StoryEnvelope>(k, this.idb);
      if (envelope === undefined) continue;
      const expiresAt = envelope.story.metadata.createdAt + STORY_TTL_SECONDS;
      if (at > expiresAt) {
        await del(k, this.idb);
      }
    }
  }

  /** Wipe every story in this store instance (test / debug helper). */
  async resetAll(): Promise<void> {
    const allKeys = await keys<string>(this.idb);
    for (const k of allKeys) {
      await del(k, this.idb);
    }
  }
}

// ---------------------------------------------------------------------------
// IDB key derivation
// ---------------------------------------------------------------------------

/**
 * Derives a stable IndexedDB key string from a storyId.
 *
 * SHA-256 of the storyId bytes produces a fixed 64-hex string key so the
 * directory of IndexedDB keys does not enumerate storyIds in plaintext.
 *
 * Mirrors iOS `StoryStore.fileURL(for:)` which SHA256s the storyId.
 */
async function idbKey(storyId: string): Promise<string> {
  const data = new TextEncoder().encode(storyId);
  const hashBuf = await crypto.subtle.digest("SHA-256", data);
  const hex = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `story:${hex}`;
}
